import React, { useRef } from "react";
import { Box, Container, Typography, Button, Grid, CircularProgress } from "@mui/material";
import { Link } from 'react-router-dom';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import axiosClient from "axiosClient";
import backgroundImage4 from '../../../assets/images/home/bg4.png';
import { useQuery } from '@tanstack/react-query';

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";

export default function SectionSix() {
  const swiperRef = useRef(null);

  // --- DATA FETCHING (React Query) ---
  const { data: rawArticles = [], isLoading: loading } = useQuery({
    queryKey: ['news'], // Shared key with News.jsx
    queryFn: async () => {
      const response = await axiosClient.get("/article-posts");
      return response.data.data || [];
    },
  });

  // --- DATA TRANSFORMATION ---
  // We map the raw API data to the format SectionSix needs
  const slides = rawArticles.map(post => ({
    id: post.id,
    image: post.mainImage || post.cardPic, 
    title: post.cardTitle,
    description: post.cardDesc,
    fullDescription: post.article,
    rawPost: post 
  }));

  return (
    <Box
      sx={{
        backgroundImage: `url(${backgroundImage4})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: '#1c1c1c',
        position: 'relative',
        overflow: 'hidden',
        // minHeight: '800px'
      }}
    >
      <Container maxWidth="lg" sx={{ py: 15, }}>

        {/* HEADER TEXT */}
        <Box sx={{mx: { md:3, xs: 3, sm: 0 },}}>
          <Typography variant="h5" sx={{ fontSize: {md: '1.7rem', xs: '2rem'} ,mb: 1, fontWeight: 'bold', color: '#00b5e2' }}>
            WHAT FUEL US 
          </Typography>
        
          <Typography
            variant="h2"
            fontWeight={800}
            sx={{ 
              mb: { xs: 3, lg: 5 },  
              mt: 1,
              color: '#0a1a3b', 
              letterSpacing: .5
            }}
          >
            MAKING A WORLD OF DIFFERENCE
          </Typography>

          <Typography variant="h3" sx={{color: '#5f5f5f', mb: 5, fontWeight: 350}}>
            Here at Centrum Fuel, we believe in doing our part to improve the world and make a difference. 
            We support a healthy relationship between environment, our people and our community.
          </Typography>
        </Box>

        {/* DYNAMIC CONTENT SWIPER */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : slides.length > 0 ? (
          
          <Swiper
            onBeforeInit={(swiper) => {
                swiperRef.current = swiper;
            }}
            modules={[Navigation]} 
            spaceBetween={50}
            slidesPerView={1}
            loop={true} 
            grabCursor={true} 
            speed={800} 
            style={{ width: "100%", paddingBottom: '50px' }} 
          >
            {slides.map((slide, index) => {
                // Logic for Next/Prev titles
                const nextIndex = (index + 1) % slides.length;
                const prevIndex = (index - 1 + slides.length) % slides.length;
                const nextTitle = slides[nextIndex]?.title;
                const prevTitle = slides[prevIndex]?.title;

                return (
                    <SwiperSlide key={slide.id || index}>
                        <Grid container spacing={6} alignItems="flex-start"> 

                            {/* LEFT IMAGE – WIDER */}
                            <Grid item xs={12} md={8}>
                                <Box
                                  component="img"
                                  src={slide.image}
                                  alt={slide.title}
                                  sx={{
                                    width: "100%",
                                    objectFit: "cover",
                                    maxHeight: 420, 
                                  }}
                                />
                            </Grid>

                            {/* RIGHT CONTENT – NARROWER */}
                            <Grid 
                              item 
                              xs={12} 
                              md={4}
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                height: { md: 450 }, 
                                justifyContent: "space-between",
                              }}
                            >
                              {/* TOP: TEXT BOX WITH GREEN BAR */}
                              <Box
                                sx={{
                                  position: "relative",
                                  backgroundColor: "#ffffffff",
                                  mt: { lg: -3 }, 
                                  py: 3,
                                  px: 2,
                                  borderRadius: 4,
                                  ml: { lg: -15 }, 
                                  zIndex: 10,
                                  mx: { xs: 1, sm: 0 },
                                  "::after": {
                                    content: '""',
                                    position: "absolute",
                                    right: 0,
                                    top: { md: "20px", sm: '1px', xs: '1px' },       
                                    height: { md: "100px", sm: '50px', xs: '50px' },    
                                    width: "1.5px",
                                    backgroundColor: "#B4D434",
                                    borderRadius: "2px"
                                  }
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    fontSize: "1.90rem", 
                                    mt: { xs: -7, sm: -5, md: -2, lg: 1 }, 
                                    ml: { lg: 1 },
                                  }}
                                >
                                  {slide.title}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: {md: "1.6rem", xs: "1.8rem"},
                                    color: '#adaaaaff',
                                    mt: 2,
                                    ml: { lg: 1 },
                                    display: '-webkit-box',
                                    overflow: 'hidden',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 3, 
                                  }}
                                >
                                  {slide.description}
                                </Typography>
                              </Box>

                              {/* BOTTOM: STACKED BUTTONS */}
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-end",
                                  textAlign: "end",
                                  gap: 2.5,
                                  mt: 'auto', 
                                  mb: 0,      
                                }}
                              >
                                {/* READ MORE BUTTON */}
                                <Button
                                  component={Link}
                                  to="/about/news_details"
                                  state={{
                                      news: slide.rawPost,
                                      newsData: rawArticles,
                                  }}
                                  variant="contained"
                                  sx={{
                                    bgcolor: "#B4D434",
                                    color: "#ffffff",
                                    borderRadius: "30px",
                                    px: 4,
                                    // py: 1,
                                    fontWeight: 'bold',
                                    fontSize: "1.5rem",
                                    mt: {lg: 'auto', md: 'auto', xs: 5},
                                    letterSpacing: "6px",
                                    textTransform: "uppercase",
                                    "&:hover": { bgcolor: "#9FBD2E" },

                                  }}
                                >
                                  READ MORE
                                </Button>

                                {/* NEXT BUTTON */}
                                <Typography
                                  onClick={() => swiperRef.current?.slideNext()}
                                  sx={{
                                    cursor: "pointer",
                                    color: "#6E6E6E",
                                    // letterSpacing: "1px",
                                    fontSize: {md: "1.6rem", xs: "2.1rem"},
                                    fontWeight: 700,
                                    letterSpacing: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    "&:hover": { color: "#00b5e2" },
                                    userSelect: "none"
                                  }}
                                >
                                  NEXT - {nextTitle}
                                  <ArrowForwardIosIcon sx={{ fontSize: "4rem" }} />
                                </Typography>

                                {/* PREV BUTTON */}
                                <Typography
                                  onClick={() => swiperRef.current?.slidePrev()}
                                  sx={{
                                    cursor: "pointer",
                                    color: "#6E6E6E",
                                    letterSpacing: "1px",
                                    fontSize: {md: "1.6rem", xs: "2.1rem"},
                                    fontWeight: 700,
                                    letterSpacing: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    "&:hover": { color: "#00b5e2" },
                                    userSelect: "none"
                                  }}
                                >
                                  PREV - {prevTitle}
                                  <ArrowBackIosNewIcon sx={{ fontSize: "4rem" }} />
                                </Typography>
                              </Box>
                            </Grid>
                        </Grid>
                    </SwiperSlide>
                );
            })}
          </Swiper>

        ) : (
          <Typography sx={{ textAlign: "center", py: 10, fontSize: "1.2rem", color: "#666" }}>
            No updates found.
          </Typography>
        )}

      </Container>
    </Box>
  );
}